// OAuth 2.0 Authorization Code + PKCE flow against Cognito Hosted UI.
//
// On app boot we run bootstrap() before rendering. Three states:
//   1. Stored tokens, valid: render the app
//   2. URL has ?code=...: finish the OAuth exchange, store tokens, clean URL
//   3. Otherwise: redirect to Cognito Hosted UI to log in
//
// Tokens live in sessionStorage so they survive tab refresh but die on tab
// close. Avoid localStorage — XSS exfiltration risk. Avoid cookies because
// CloudFront + custom domain + cookie scopes is a maze and the API
// authorizer reads Authorization headers anyway.

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/admin/`;

const SS_VERIFIER = "auth.code_verifier";
const SS_TOKENS = "auth.tokens";

interface Tokens {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_at: number; // ms since epoch
}

// Returns true if the user is authenticated (has tokens or just completed
// an OAuth callback). Returns false if no session — caller should render
// the Landing page so the user can pick Sign In or Sign Up.
//
// Notably: this no longer auto-redirects on missing tokens. The previous
// behavior was hostile UX (user clicks our URL, immediately gets dropped
// onto an unfamiliar Cognito domain with no context).
export async function bootstrap(): Promise<boolean> {
  if (!COGNITO_DOMAIN || !CLIENT_ID) {
    // eslint-disable-next-line no-console
    console.error("VITE_COGNITO_DOMAIN or VITE_COGNITO_CLIENT_ID missing");
    return false;
  }

  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");

  if (code) {
    // Coming back from Hosted UI — finish the exchange.
    await exchangeCode(code);
    // Clean ?code=&state= out of the address bar so refresh doesn't re-exchange.
    window.history.replaceState({}, "", window.location.pathname);
    return true;
  }

  return getStoredTokens() != null;
}

// Public sign-in initiator: stores PKCE verifier and redirects to Cognito's
// Hosted UI login form. Use from the Landing page's "Sign in" button.
export async function signIn(): Promise<void> {
  await startAuth("/oauth2/authorize");
}

// Public sign-up initiator: same as signIn but lands on Cognito's signup
// form instead. Use from the Landing page's "Sign up" button.
export async function signUp(): Promise<void> {
  await startAuth("/signup");
}

export function getIdToken(): string | null {
  const t = getStoredTokens();
  if (!t) return null;
  if (Date.now() >= t.expires_at) {
    clearTokens();
    return null;
  }
  return t.id_token;
}

export function getUserEmail(): string | null {
  const t = getStoredTokens();
  if (!t) return null;
  try {
    const claims = JSON.parse(atob(t.id_token.split(".")[1]));
    return typeof claims.email === "string" ? claims.email : null;
  } catch {
    return null;
  }
}

export function getUserSub(): string | null {
  const t = getStoredTokens();
  if (!t) return null;
  try {
    const claims = JSON.parse(atob(t.id_token.split(".")[1]));
    return typeof claims.sub === "string" ? claims.sub : null;
  } catch {
    return null;
  }
}

export function getUserGroups(): string[] {
  const t = getStoredTokens();
  if (!t) return [];
  try {
    const claims = JSON.parse(atob(t.id_token.split(".")[1]));
    const raw = claims["cognito:groups"];
    if (Array.isArray(raw)) return raw.filter((g) => typeof g === "string");
    return [];
  } catch {
    return [];
  }
}

export function hasAnyGroup(...groups: string[]): boolean {
  const mine = new Set(getUserGroups());
  return groups.some((g) => mine.has(g));
}

export function logout(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: REDIRECT_URI,
  });
  window.location.href = `${COGNITO_DOMAIN}/logout?${params}`;
}

// --- internals ---

// PKCE setup + redirect to one of Cognito's Hosted UI entry points
// (/oauth2/authorize for login, /signup for new accounts). Same params
// for both since Cognito takes the same OAuth metadata either way.
async function startAuth(endpoint: "/oauth2/authorize" | "/signup"): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  sessionStorage.setItem(SS_VERIFIER, verifier);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: REDIRECT_URI,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.href = `${COGNITO_DOMAIN}${endpoint}?${params}`;
}

async function exchangeCode(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  if (!verifier) {
    // No stored verifier — probably a stale callback URL. Restart login.
    await startAuth("/oauth2/authorize");
    return;
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  const res = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${text}`);
  }
  const data = await res.json();

  storeTokens({
    id_token: data.id_token,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + Number(data.expires_in) * 1000,
  });
  sessionStorage.removeItem(SS_VERIFIER);
}

function getStoredTokens(): Tokens | null {
  const raw = sessionStorage.getItem(SS_TOKENS);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return null;
  }
}

function storeTokens(t: Tokens): void {
  sessionStorage.setItem(SS_TOKENS, JSON.stringify(t));
}

function clearTokens(): void {
  sessionStorage.removeItem(SS_TOKENS);
  sessionStorage.removeItem(SS_VERIFIER);
}

function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64url(new Uint8Array(hash));
}

function base64url(bytes: Uint8Array): string {
  // btoa expects a binary string; build it character-by-character to avoid
  // String.fromCharCode(...bigArray) hitting the argument-count cap.
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
